import React from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  ResponsiveContainer, RadarChart, PolarGrid,
  PolarAngleAxis as PolarAngleAxisBase, PolarRadiusAxis as PolarRadiusAxisBase,
  Radar, Tooltip,
} from 'recharts';
import { HolisticStudent } from '../../../types';
import { TermCard } from '../../../components/shared';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PolarAngleAxis = PolarAngleAxisBase as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PolarRadiusAxis = PolarRadiusAxisBase as any;

const SCORE_CARDS = [
  { label: 'Behavior Score',   bg: 'from-indigo-50 to-violet-50',   border: 'border-indigo-100',  stroke: '#6366F1' },
  { label: 'Participation',    bg: 'from-violet-50 to-purple-50',   border: 'border-violet-100',  stroke: '#7C3AED' },
  { label: 'Social Comfort',   bg: 'from-amber-50 to-orange-50',    border: 'border-amber-100',   stroke: '#F59E0B' },
  { label: 'Learning Comfort', bg: 'from-purple-50 to-indigo-50',   border: 'border-purple-100',  stroke: '#8B5CF6' },
];

const ScoreRing: React.FC<{ label: string; value: number; stroke: string; bg: string; border: string }> = ({ label, value, stroke, bg, border }) => {
  const r = 30;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(100, value) / 100) * circ;
  return (
    <div className={`rounded-xl border ${border} bg-gradient-to-br ${bg} p-4 shadow-sm flex items-center gap-3`}>
      <svg width="68" height="68" viewBox="0 0 68 68" className="flex-shrink-0">
        <circle cx="34" cy="34" r={r} fill="none" stroke="#E5E7EB" strokeWidth="7" />
        <circle
          cx="34" cy="34" r={r}
          fill="none" stroke={stroke} strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={offset}
          transform="rotate(-90 34 34)"
          style={{ transition: 'stroke-dashoffset 1s ease' }}
        />
        <text x="34" y="39" textAnchor="middle" fontSize="12" fontWeight="700" fill={stroke}>{value}</text>
      </svg>
      <div>
        <p className="text-xs text-gray-400 leading-snug">{label}</p>
        <p className="text-sm font-semibold text-gray-700 mt-0.5">/ 100</p>
      </div>
    </div>
  );
};

const BehaviorTab: React.FC = () => {
  const { student } = useOutletContext<{ student: HolisticStudent }>();

  const radarData = [
    { subject: 'Confidence',       value: student.behaviorScore },
    { subject: 'Participation',    value: Math.min(100, student.behaviorScore + 8) },
    { subject: 'Learning Comfort', value: Math.min(100, Math.round((student.academic.term1_competency + student.academic.term2_competency) / 2)) },
    { subject: 'Social Comfort',   value: student.peerScore },
  ];

  const scoreValues = [
    student.behaviorScore,
    Math.min(100, student.behaviorScore + 8),
    student.peerScore,
    Math.min(100, Math.round((student.academic.term1_competency + student.academic.term2_competency) / 2)),
  ];

  const behaviorLevel = student.behaviorScore >= 80 ? 'Excellent' : student.behaviorScore >= 60 ? 'Good' : 'Needs Attention';
  const badgeCls = behaviorLevel === 'Excellent'
    ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
    : behaviorLevel === 'Good'
    ? 'bg-violet-50 text-violet-700 border-violet-200'
    : 'bg-rose-50 text-rose-700 border-rose-200';

  return (
    <div className="space-y-5">
      {/* Score rings */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {SCORE_CARDS.map((c, i) => (
          <ScoreRing key={c.label} label={c.label} value={scoreValues[i]} stroke={c.stroke} bg={c.bg} border={c.border} />
        ))}
      </div>

      {/* Radar */}
      <div className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50/50 to-violet-50/30 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-semibold text-gray-700">Behavior Radar</p>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${badgeCls}`}>{behaviorLevel}</span>
        </div>
        <ResponsiveContainer width="100%" height={270}>
          <RadarChart data={radarData}>
            <defs>
              <radialGradient id="bhvFill" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#7C3AED" stopOpacity={0.22} />
                <stop offset="100%" stopColor="#7C3AED" stopOpacity={0.04} />
              </radialGradient>
            </defs>
            <PolarGrid stroke="#EDE9FE" />
            <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: '#6B7280' }} />
            <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10, fill: '#9CA3AF' }} />
            <Radar dataKey="value" stroke="#7C3AED" fill="url(#bhvFill)" strokeWidth={2} dot={{ fill: '#7C3AED', r: 3, stroke: '#fff', strokeWidth: 1.5 }} />
            <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #EDE9FE', fontSize: 12, background: 'rgba(255,255,255,0.95)' }} />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Behavior detail cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TermCard title="Self With Friends" items={[
          { term: 'Term 1', text: student.behavior.self_with_friends_t1 },
          { term: 'Term 2', text: student.behavior.self_with_friends_t2 },
        ]} />
        <TermCard title="Things Student Enjoys" items={[
          { term: 'Term 1', text: student.behavior.self_enjoy_most_t1 },
          { term: 'Term 2', text: student.behavior.self_enjoy_most_t2 },
        ]} />
        <TermCard title="Things Found Difficult" items={[
          { term: 'Term 1', text: student.behavior.self_find_difficult_t1 },
          { term: 'Term 2', text: student.behavior.self_find_difficult_t2 },
        ]} />
      </div>
    </div>
  );
};

export default BehaviorTab;
